import { VIEW_LABELS, faceAspect } from "./data/collections";
import { CATEGORY_LABELS, CATEGORY_ORDER, pick, useLang } from "./i18n";

// ภาพประกอบ landmark = ส่วนหนึ่งของ texture จริง ตำแหน่งตาม UV ของ hotspot (ไม่ใช้ภาพจากแหล่งอื่น)
// ส่วน "จุดเด่นบนลวดลาย" ใต้เรื่องราว — ซ่อนไว้ (V2: ดูจุดเด่นด้วย tooltip บนถุงและในหน้าซูมแทน) เปลี่ยนเป็น true เพื่อแสดงอีกครั้ง
const SHOW_HIGHLIGHTS = false;

const ZOOM = 1.25; // เท่าของ texture ในกรอบภาพย่อ (step ละ 0.25): 1 = เห็นเต็มความกว้างของด้านนั้น (ต่ำสุด)
const BOX_ASPECT = 4 / 3; // กรอบภาพย่อ (.bce-landmark-img)

// จัดให้จุดอยู่กลางกรอบ: position % = (t·z − 0.5) / (z − 1) เมื่อภาพใหญ่กว่ากรอบ z เท่าในแกนนั้น
const centerOn = (t, z) => (z <= 1.001 ? 50 : Math.min(1, Math.max(0, (t * z - 0.5) / (z - 1))) * 100);

const thumbStyle = (collection, h) => {
  const aspect = faceAspect(collection, h.view) * BOX_ASPECT;
  const zx = Math.max(ZOOM, 1 / aspect); // ด้านที่เตี้ยกว่ากรอบ (ใต้ถุง) ขยายให้เต็มความสูง ไม่มีขอบว่าง
  const zy = zx * aspect;
  return {
    backgroundImage: `url("${collection.textures[h.view]}")`,
    backgroundSize: `${zx * 100}% auto`,
    backgroundPosition: `${centerOn(h.u, zx)}% ${centerOn(h.v, zy)}%`,
  };
};

// จัดกลุ่มตามหมวด: สถานที่สำคัญ → เอกลักษณ์เมือง → ของดีประจำจังหวัด (ไม่มีหมวดไปท้ายสุด)
const groupByCategory = (hotspots) => {
  const groups = CATEGORY_ORDER.map((cat) => ({ cat, items: hotspots.filter((h) => h.category === cat) }));
  const rest = hotspots.filter((h) => !CATEGORY_ORDER.includes(h.category));
  if (rest.length) groups.push({ cat: null, items: rest });
  return groups.filter((g) => g.items.length);
};

export default function StoryPanel({ collection, selectedHotspot, onHotspotSelect }) {
  const { lang, t } = useLang();
  const groups = groupByCategory(collection.hotspots);

  return (
    <aside className="bce-story" id="bce-story" aria-labelledby="bce-story-title">
      <h2 id="bce-story-title">{t("storyTitle")}</h2>
      <p className="bce-story-sub">{t("storySub")}</p>
      <p className="bce-story-text">{pick(collection, "story", lang)}</p>
      {SHOW_HIGHLIGHTS && (
        <>
      <hr />
      <h3>{t("highlights")}</h3>
      {!collection.hotspots.length && <p className="bce-story-text">{t("noHotspots")}</p>}
      {groups.map(({ cat, items }) => (
        <section key={cat || "other"} className="bce-landmark-group">
          {cat && (
            <h4 className={`bce-cat bce-cat--${cat}`}>
              {CATEGORY_LABELS[cat][lang]} <small>({items.length})</small>
            </h4>
          )}
          <div className="bce-landmarks">
            {items.map((h) => {
              const selected = selectedHotspot?.id === h.id;
              return (
                <button
                  key={h.id}
                  type="button"
                  className={`bce-landmark${selected ? " is-selected" : ""}`}
                  aria-pressed={selected}
                  onClick={() => onHotspotSelect(h.id, { rotate: true })}
                >
                  <span className="bce-landmark-img" style={thumbStyle(collection, h)} aria-hidden="true" />
                  <b>{pick(h, "title", lang)}</b>
                  <small>
                    {selected ? t("viewing") : ""}
                    {VIEW_LABELS[h.view][lang]}
                  </small>
                </button>
              );
            })}
          </div>
        </section>
      ))}

      {collection.hotspots.length > 0 && (
        <div className="bce-focused" aria-live="polite">
          {selectedHotspot ? (
            <>
              <b>{pick(selectedHotspot, "title", lang)}</b>
              {selectedHotspot.category && (
                <span className={`bce-focused-view bce-cat--${selectedHotspot.category}`}>{CATEGORY_LABELS[selectedHotspot.category][lang]}</span>
              )}
              <span className="bce-focused-view">{VIEW_LABELS[selectedHotspot.view][lang]}</span>
              <p>{pick(selectedHotspot, "description", lang)}</p>
            </>
          ) : (
            <p>{t("selectHint")}</p>
          )}
        </div>
      )}
        </>
      )}
    </aside>
  );
}
